import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

import { Store } from 'components/common/types/Store.types';
import {
  addDealLineVenueTaxonomy,
  changeDealLineVenueTaxonomy,
  clearDealLineVenueTaxonomies,
  removeDealLineVenueTaxonomy,
} from 'store/dealManagement/reducer';
import withCancelRequest from 'components/hocs/withCancelRequest';
import MultiLevelDropdown from 'lib/MultiLevelDropdown';
import { Option, OptionCode } from 'components/common/types/DropdownItem.types';
import { getVenueTaxonomyLookup } from 'modules/api/lookups';
import { deepFind } from 'utils/deepFind';
import { isEqual } from 'lodash';
import { getLeafNodes, getSelectedAncestors } from 'utils/multiDimensionalListUtils';
import Chip, { Color, Width } from 'lib/Chip';
import { VenueTaxonomyProps, VenueTaxonomyResponse } from './VenueTaxonomy.types';
import { mapVenueTaxonomyToMultiLevelData } from './utils';

const VenueTaxonomy: React.FC<VenueTaxonomyProps> = ({
  readOnly = false,
  isMultiLevelDropdownPositionFixed = false,
  isTitle = true,
  cancelFunctions,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const dispatch = useDispatch();
  const venueTaxonomies = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.venueTaxonomies);

  const [venueTaxonomyOptions, setVenueTaxonomyOptions] = useState<Option[]>([]);

  useEffect(() => {
    const getVenueTaxonomyData = async (): Promise<void> => {
      try {
        const result: VenueTaxonomyResponse[] = await getVenueTaxonomyLookup(cancelFunctions);
        setVenueTaxonomyOptions(mapVenueTaxonomyToMultiLevelData(result));
      } catch {} // eslint-disable-line no-empty
    };
    getVenueTaxonomyData();
  }, []);

  const selectedChips = venueTaxonomyOptions.flatMap((option) => getSelectedAncestors(option, venueTaxonomies));

  const addVenueTaxonomy = (venueTaxonomy: Option): void => {
    dispatch(addDealLineVenueTaxonomy(venueTaxonomy.code as number));
  };

  const removeVenueTaxonomy = (venueTaxonomy: Option): void => {
    dispatch(removeDealLineVenueTaxonomy(venueTaxonomy.code as number));
  };

  useEffect(() => {
    if (!venueTaxonomyOptions.length || !venueTaxonomies.length) return;

    const selectedVenueTaxonomyOptions = venueTaxonomies.map((venueTaxonomyCode) => {
      return deepFind(venueTaxonomyOptions, venueTaxonomyCode);
    });

    const selectedVenueTaxonomyCodesAndParentCodes = selectedVenueTaxonomyOptions.reduce<OptionCode[]>(
      (allCodes, selectedVenueTaxonomy) => {
        if (selectedVenueTaxonomy && !selectedVenueTaxonomy.children) {
          allCodes.push(selectedVenueTaxonomy.code);
        }

        if (
          selectedVenueTaxonomy &&
          selectedVenueTaxonomy.parentCode &&
          !allCodes.includes(selectedVenueTaxonomy.parentCode)
        ) {
          allCodes.push(selectedVenueTaxonomy.parentCode);
        }

        return allCodes;
      },
      [],
    );

    if (!isEqual(selectedVenueTaxonomyCodesAndParentCodes, venueTaxonomies)) {
      dispatch(changeDealLineVenueTaxonomy(selectedVenueTaxonomyCodesAndParentCodes as number[]));
    }
  }, [venueTaxonomyOptions, venueTaxonomies]);

  return (
    <div
      data-test-id="venue-taxonomy"
      className={cx('px-12 py-6', {
        relative: isMultiLevelDropdownPositionFixed,
      })}
    >
      {isTitle && <p className="sub-header-base text-neutral-950-opacity-60 mb-2.5">Select venue taxonomy</p>}
      <div className="flex flex-row space-x-3.5" data-test-id="venue-taxonomy-chips-container">
        <MultiLevelDropdown
          options={venueTaxonomyOptions}
          value={venueTaxonomies}
          onChangeItem={(node, checked) => (checked ? addVenueTaxonomy(node) : removeVenueTaxonomy(node))}
          onRemoveAll={() => dispatch(clearDealLineVenueTaxonomies())}
          isDisabled={readOnly}
          isPositionFixed={isMultiLevelDropdownPositionFixed}
          dataTestId="venue-taxonomy-multi-level-dropdown"
        />
        <div className="flex flex-wrap gap-x-7 gap-y-1">
          {selectedChips.length
            ? selectedChips.map((chip) => (
                <Chip
                  key={chip.code}
                  isDisabled={readOnly}
                  dataTestId="environment-chip"
                  label={chip.name}
                  onRemove={() => getLeafNodes(chip).forEach((node) => removeVenueTaxonomy(node))}
                  color={Color.PRIMARY}
                  width={Width.FULL}
                  truncateLabel={false}
                />
              ))
            : null}
        </div>
      </div>
    </div>
  );
};

export default withCancelRequest(VenueTaxonomy);
